import { JurisdictionTypeEnum } from '@zaoblako/shared/modules/client/enums/jurisdiction-type.enum';
import { JurisdictionTypeEnumI18n } from '@zaoblako/shared/modules/client/i18n/jurisdiction-type-enum.i18n';
import { PersonTypeEnum } from '@zaoblako/shared/modules/client/enums/person.type.enum';
import { PersonTypeEnumI18n } from '@zaoblako/shared/modules/client/i18n/person.type.enum.i18n';
import { PersonPassportDataDto, type PersonPassportDataInterface } from '@zaoblako/shared/modules/client/dtos/person/person-passport-data.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { ClientDtoKeysEnum } from '@zaoblako/shared/modules/client/enums/client-dto-keys.enum';
import { russianTinSchema } from '@zaoblako/shared/modules/serde/schemas/russian-tin.schema';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';

import * as yup from 'yup';

/** Common person (company or counterparty) create or update request body */
export interface PersonFormInterface {
  /** Display name */
  name: string;
  /** Person jurisdiction */
  jurisdiction?: JurisdictionTypeEnum;
  /** Taxpayer Identification Number (INN, rus. "ИНН") */
  inn?: string;
  /** Taxpayer Registration Reason Code (KPP, rus. "КПП") */
  kpp?: string;
  /** Primary State Registration Number (OGRN, rus. "ОГРН") */
  ogrn?: string;
  /** All-Russian Classifier of Enterprises and Organizations Number (OKPO, rus. "ОКПО") */
  okpo?: string;
  /** Manager name */
  manager?: string;
  /** Primary State Registration Number for Individual Entrepreneur (OGRNIP, rus. "ОГРНИП") */
  ogrnip?: string;
  /** Person geographic address */
  address?: string;
  /** Person real mail address */
  postAddress?: string;
  /** Main phone number */
  phone?: string;
  /** Person web site address */
  website?: string;
  /** Person main email address */
  email?: string;
  /** Person type */
  personType: PersonTypeEnum;
  /** Manager passport data */
  passport?: PersonPassportDataInterface;
}

/** Common person (company or counterparty) create or update request body - DTO schema */
export const PersonFormDto = SerdeDtoClass.create<PersonFormInterface>(ClientDtoKeysEnum.PERSON_FORM_DTO, {
  name: {
    label: 'Название',
    serializer: yup.string(),
  },
  jurisdiction: {
    optional: true,
    default: JurisdictionTypeEnum.RUSSIAN_FEDERATION,
    label: 'Юрисдикция',
    serializer: commonEnumValidator(JurisdictionTypeEnum, JurisdictionTypeEnumI18n),
  },
  inn: {
    optional: true,
    label: 'ИНН',
    serializer: russianTinSchema,
  },
  ogrn: {
    optional: true,
    label: 'ОГРН',
    serializer: yup.string(),
  },
  okpo: {
    optional: true,
    label: 'ОКПО',
    serializer: yup.string(),
  },
  kpp: {
    optional: true,
    label: 'КПП',
    serializer: yup
      .string()
      .matches(/^\d{9}$/im, DtoMessageEnum.INCORRECT_FORMAT),
  },
  ogrnip: {
    optional: true,
    label: 'ОГРНИП',
    serializer: yup.string(),
  },
  manager: {
    optional: true,
    label: 'Генеральный директор',
    serializer: yup.string(),
  },
  phone: {
    optional: true,
    label: 'Телефон',
    serializer: yup.string(),
  },
  website: {
    optional: true,
    label: 'Веб-сайт',
    serializer: yup.string(),
  },
  email: {
    optional: true,
    label: 'Email',
    serializer: yup.string(),
  },
  address: {
    optional: true,
    label: 'Адрес',
    serializer: yup.string(),
  },
  postAddress: {
    optional: true,
    label: 'Почтовый адрес',
    serializer: yup.string(),
  },
  personType: {
    label: 'Тип',
    serializer: commonEnumValidator(PersonTypeEnum, PersonTypeEnumI18n),
  },
  passport: {
    optional: true,
    nested: PersonPassportDataDto,
    label: 'Паспортные данные',
    serializer: (_, nested) => nested,
  },
});
